import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { IconButton, Link, Typography } from '@mui/material'
import EstadoAsistencia from '../../../../../components/EstadoAsistencia/EstadoAsistencia'
import PageLoading from '../../../../../components/PageLoading/PageLoading'
import { useNotification } from '../../../../../context/NotificationManager/NotificationManager'
import {
  getAsistenciasLeadPendientes,
  updateAsistencia
} from '../../../../../utils/api/asistencias'
import { getAuth } from '../../../../../utils/auth'
import { dateToString } from '../../../../../utils/date'
import { downloadCSVFile, getCSVContent } from '../../../../../utils/csv'
import { dateTimeSort, textFilter } from '../../../../../utils/table'
import TableHeader from '../../../../../components/TableHeader/TableHeader'
import InformationTable from '../../../../../components/Table/InformationTable'
import css from './PruebasPendientes.module.css'

const SinProfesor = () => <span className={css.placeholder}>Sin profesor</span>

const PruebasPendientes = () => {
  const userData = getAuth()

  const queryClient = useQueryClient()
  const notification = useNotification()

  const [search, setSearch] = useState('')

  const { isLoading, data: pruebaList = [] } = useQuery({
    queryKey: [
      'planificacion',
      'asistencia',
      'list',
      'feedback',
      'sin-asistencia'
    ],
    queryFn: () =>
      getAsistenciasLeadPendientes()
        .then(datos =>
          datos.map(asistencia => ({
            ...asistencia,
            fecha: dateToString(asistencia.fecha)
          }))
        )
        .catch(err => {
          notification.error({
            title: 'Error al recuperar las pruebas pendientes',
            content: err
          })
          return []
        })
  })

  const { isPending: isUpdatingAsistencia, mutate: modificarAsistencia } =
    useMutation({
      mutationFn: updateAsistencia,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['planificacion', 'asistencia', 'list']
        })
      },
      onError: err => {
        notification.error({
          title: 'Error al actualizar la asistencia',
          content: err
        })
      }
    })

  const handleExport = () => {
    const content = getCSVContent({
      columns: [
        'Lead',
        'Centro',
        'Fecha',
        'Hora',
        'Asignatura',
        'Clase',
        'Profesor',
        'Observaciones'
      ],
      data: pruebaList,
      transform: c => [
        c.feedbackNombre,
        c.centro,
        c.fecha,
        c.hora,
        `${c.asignatura} - ${c.nivel}`,
        c.clase,
        c.profesor,
        c.observaciones
      ]
    })
    downloadCSVFile(content, `pruebas-pendientes`)
  }

  const filterSearch = prueba => {
    return textFilter({
      object: prueba,
      fields: [
        'feedbackNombre',
        'centro',
        'fecha',
        'hora',
        'asignatura',
        'tipo',
        'nivel',
        'clase',
        'profesor',
        'observaciones'
      ],
      search: search
    })
  }

  const handleAsistenciaClick = (estado, asistencia) => {
    if (isUpdatingAsistencia) return
    modificarAsistencia({ id: asistencia.id, asistencia: estado })
  }

  return (
    <PageLoading isLoading={isLoading}>
      <TableHeader
        actions={
          <div className={css.actions}>
            <IconButton
              className={css.download}
              size='small'
              color='primary'
              onClick={handleExport}
            >
              <i className='material-icons'>download</i>
            </IconButton>
          </div>
        }
        title={`${pruebaList.length} pruebas pendientes`}
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
      />
      {pruebaList.length === 0 && (
        <div className={css.empty}>
          <Typography className={css.placeholder} variant='body1'>
            No hay pruebas pendientes
          </Typography>
        </div>
      )}
      {pruebaList.length > 0 && (
        <InformationTable
          details={[
            { title: 'Lead', key: 'lead', sortKey: 'leadNombre' },
            {
              title: 'Fecha',
              key: 'fecha',
              sortFunction: dateTimeSort,
              sortKey: 'fechaInicio'
            },
            { title: 'Asignatura', key: 'asignatura' },
            { title: 'Profesor', key: 'profesor' },
            {
              title: 'Asistencia',
              key: 'asistencia',
              align: 'center',
              sortDisabled: true
            },
            { title: 'Observaciones', key: 'observaciones', sortDisabled: true }
          ]}
          data={pruebaList.filter(filterSearch).map(prueba => ({
            leadNombre: prueba.feedbackNombre,
            lead: (
              <Link
                className={css.link}
                href={`/leads/${prueba.feedbackId}/ficha`}
              >
                {prueba.feedbackNombre}
              </Link>
            ),
            fecha: `${prueba.fecha} ${prueba.hora}`,
            fechaInicio: `${prueba.fecha} ${prueba.hora.substring(0, 5)}`,
            asignatura: prueba.asignatura,
            profesor: prueba.profesor || <SinProfesor />,
            asistencia: (
              <EstadoAsistencia
                asistencia={prueba}
                centro={userData.centro}
                onClick={estado => handleAsistenciaClick(estado, prueba)}
              />
            ),
            observaciones: prueba.observaciones
          }))}
        />
      )}
    </PageLoading>
  )
}

export default PruebasPendientes
